/**
 * A-12 / S-02 — admin+support shared ticket inbox. Tabs (unclaimed/mine/all), claim, reply,
 * resolve, reopen. Reused by both the support surface and the admin surface.
 */

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import { describeError } from "../../api/errors";
import { useIdempotencyKey } from "../../api/useIdempotencyKey";
import {
  inboxClaim,
  inboxGet,
  inboxQueue,
  inboxReply,
  inboxReopen,
  inboxResolve,
  loadInboxAttachment,
  messageFiles,
  type SupportTicket,
} from "../../api/support";
import { AttachmentPicker } from "../../components/ui/AttachmentPicker";
import { AttachmentPreview, type AttachmentTarget } from "../../components/ui/AttachmentPreview";
import { ExportButton } from "../../components/ui/ExportButton";
import { Modal } from "../../components/ui/Modal";
import {
  byPageNumber,
  EXPORT_PAGE_SIZE,
  sheetDateTime,
  sheetLabel,
  type ExportColumn,
} from "../../lib/tableExport";
import { StatusPill } from "../patient/Support";

const TABS = [
  { key: "unclaimed", label: "Unclaimed" },
  { key: "mine", label: "Mine" },
  { key: "all", label: "All" },
] as const;

const EXPORT_COLUMNS: ExportColumn<SupportTicket>[] = [
  { header: "Opened (IST)", value: (t) => sheetDateTime(t.opened_at) },
  { header: "Subject", value: (t) => t.subject },
  { header: "Category", value: (t) => sheetLabel(t.category) },
  { header: "Status", value: (t) => sheetLabel(t.status) },
  { header: "From", value: (t) => sheetLabel(t.opener_role) },
  { header: "About", value: (t) => (t.context_entity_type ? `${sheetLabel(t.context_entity_type)} ${t.context_entity_id ?? ""}`.trim() : "") },
  { header: "Messages", value: (t) => t.message_count },
  { header: "First reply (IST)", value: (t) => sheetDateTime(t.first_response_at) },
  { header: "Last message (IST)", value: (t) => sheetDateTime(t.last_message_at) },
  { header: "Resolved (IST)", value: (t) => sheetDateTime(t.resolved_at) },
  { header: "Ticket ID", value: (t) => t.id },
];

export function SupportInbox() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<(typeof TABS)[number]["key"]>("unclaimed");
  // /staff/support/tickets/:id (a notification's link) opens that ticket (ACCT-11).
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeId, setActiveId] = useState<string | null>(id ?? null);
  useEffect(() => setActiveId(id ?? null), [id]);
  const closeTicket = () => {
    setActiveId(null);
    if (id) navigate("/staff/support/inbox", { replace: true });
  };
  // Every page of the tab, not only the first 25 (SUP-06).
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ["inbox", tab],
    queryFn: ({ pageParam }) => inboxQueue(tab, pageParam),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.has_more ? last.page + 1 : undefined),
  });
  const tickets = data?.pages.flatMap((p) => p.data) ?? [];
  const invalidate = () => qc.invalidateQueries({ queryKey: ["inbox"] });

  return (
    <div className="max-w-3xl space-y-5">
      <h2 className="text-lg font-semibold">Support inbox</h2>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} className={`rounded-lg px-3 py-1.5 ${tab === t.key ? "bg-brand text-brand-fg" : "border"}`}>
            {t.label}
          </button>
        ))}
        <span className="ml-auto">
          {/* Every ticket in this tab, not only the pages loaded. */}
          <ExportButton
            table="support tickets"
            columns={EXPORT_COLUMNS}
            keyOf={(t) => t.id}
            fetchPage={byPageNumber((page) => inboxQueue(tab, page, EXPORT_PAGE_SIZE))}
          />
        </span>
      </div>

      <ul className="space-y-2">
        {tickets.map((t) => (
          <li key={t.id}>
            <button onClick={() => setActiveId(t.id)} className="block w-full rounded-xl border bg-white p-4 text-left shadow-xs hover:border-brand">
              {/* A long subject wraps beside the pill instead of running into it (RESP-14). */}
              <div className="flex items-start justify-between gap-3">
                <span className="min-w-0 font-medium wrap-anywhere">{t.subject}</span>
                <StatusPill status={t.status} />
              </div>
              <div className="mt-1 text-xs text-gray-400">
                {t.opener_role} · {t.category.replace("_", " ")} · {new Date(t.last_message_at).toLocaleString()}
                {t.claimed_by_user_id ? " · claimed" : ""}
              </div>
            </button>
          </li>
        ))}
        {data && tickets.length === 0 && <p className="text-sm text-gray-400">No tickets in this tab.</p>}
      </ul>
      {hasNextPage && (
        <button onClick={() => fetchNextPage()} disabled={isFetchingNextPage} className="text-sm text-brand hover:underline disabled:opacity-50">
          {isFetchingNextPage ? "Loading…" : "Load more"}
        </button>
      )}

      <Modal open={activeId !== null} title="Ticket" onClose={closeTicket}>
        {activeId && <StaffThread ticketId={activeId} onChange={invalidate} />}
      </Modal>
    </div>
  );
}

function StaffThread({ ticketId, onChange }: { ticketId: string; onChange: () => void }) {
  const qc = useQueryClient();
  const { data: ticket } = useQuery({ queryKey: ["inbox-ticket", ticketId], queryFn: () => inboxGet(ticketId) });
  const [body, setBody] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [preview, setPreview] = useState<AttachmentTarget | null>(null);
  const refresh = () => { qc.invalidateQueries({ queryKey: ["inbox-ticket", ticketId] }); onChange(); };

  // Each says how it ended (SUP-07): a refusal — another agent claimed it first, it was reopened
  // meanwhile — was dropped and the thread stayed stale; now it is said and the ticket fetched again.
  const done = (message: string) => () => {
    toast.success(message);
    refresh();
  };
  const failed = (e: unknown) => {
    toast.error(describeError(e));
    refresh();
  };
  const claim = useMutation({ mutationFn: () => inboxClaim(ticketId), onSuccess: done("Ticket claimed."), onError: failed });
  const replyKey = useIdempotencyKey();
  const reply = useMutation({
    mutationFn: () => inboxReply(ticketId, body, files, replyKey.keyFor({ ticketId, body, files: files.map((f) => [f.name, f.size, f.lastModified]) })),
    onSuccess: () => {
      replyKey.reset();
      setBody("");
      setFiles([]);
      refresh();
    },
  });
  const resolve = useMutation({ mutationFn: () => inboxResolve(ticketId), onSuccess: done("Ticket resolved."), onError: failed });
  const reopen = useMutation({ mutationFn: () => inboxReopen(ticketId), onSuccess: done("Ticket reopened."), onError: failed });
  const acting = claim.isPending || resolve.isPending || reopen.isPending;

  if (!ticket) return <p className="text-sm text-gray-400">Loading…</p>;
  const resolved = ticket.status === "resolved";

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-3">
        <span className="min-w-0 font-medium wrap-anywhere">{ticket.subject}</span>
        <StatusPill status={ticket.status} />
      </div>
      <div className="flex flex-wrap gap-2 text-sm">
        {!ticket.claimed_by_user_id && (
          <button onClick={() => claim.mutate()} disabled={acting} className="rounded-lg border px-3 py-1.5 disabled:opacity-50">
            {claim.isPending ? "Claiming…" : "Claim"}
          </button>
        )}
        {!resolved ? (
          <button onClick={() => resolve.mutate()} disabled={acting} className="rounded-lg border px-3 py-1.5 text-emerald-700 disabled:opacity-50">
            {resolve.isPending ? "Resolving…" : "Resolve"}
          </button>
        ) : (
          <button onClick={() => reopen.mutate()} disabled={acting} className="rounded-lg border px-3 py-1.5 disabled:opacity-50">
            {reopen.isPending ? "Reopening…" : "Reopen"}
          </button>
        )}
      </div>
      <div className="max-h-64 space-y-2 overflow-y-auto">
        {ticket.messages.map((m) => (
          <div key={m.id} className={`rounded-lg p-3 text-sm ${m.author_role === ticket.opener_role ? "bg-gray-50" : "bg-brand/10"}`}>
            <div className="text-xs text-gray-400">{m.author_role} · {new Date(m.created_at).toLocaleString()}</div>
            {/* The author's line breaks stay, and a long URL or reference wraps inside the bubble (RESP-13). */}
            <div className="whitespace-pre-wrap wrap-anywhere">{m.body}</div>
            {messageFiles(m).length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {messageFiles(m).map((file) => (
                  <button
                    key={file.index}
                    type="button"
                    onClick={() => setPreview({ filename: file.filename, load: () => loadInboxAttachment(ticket.id, m.id, file.index) })}
                    className="max-w-[12rem] truncate rounded-md border bg-white px-2 py-0.5 text-xs text-brand hover:border-brand"
                    title={file.filename}
                  >
                    📎 {file.filename}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      {!resolved && (
        <form onSubmit={(e) => { e.preventDefault(); if (body.trim()) reply.mutate(); }} className="space-y-2">
          <div className="flex gap-2">
            <input value={body} onChange={(e) => setBody(e.target.value)} placeholder="Reply…" className="min-w-0 flex-1 rounded-lg border px-3 py-1.5 text-sm" />
            <button disabled={reply.isPending} className="rounded-lg bg-brand px-4 py-1.5 text-sm font-medium text-brand-fg disabled:opacity-50">Send</button>
          </div>
          <AttachmentPicker files={files} onChange={setFiles} disabled={reply.isPending} />
          {/* SUP-08: the first reply claims it, so it moves to Mine instead of out of every tab. */}
          {!ticket.claimed_by_user_id && <p className="text-xs text-gray-500">Replying claims this ticket for you.</p>}
          {reply.isError && <p className="text-sm text-red-600">{describeError(reply.error)}</p>}
        </form>
      )}
      <AttachmentPreview target={preview} onClose={() => setPreview(null)} />
    </div>
  );
}
